// Imports
import { Plugin } from '@ckeditor/ckeditor5-core';
import { ViewDocumentFragment, ViewElement, ViewText, ViewNode, ViewDocument } from '@ckeditor/ckeditor5-engine';


// Sourced from https://github.com/ckeditor/ckeditor5/blob/v47.2.0/packages/ckeditor5-link/src/autolink.ts#L22-L71
const URL_REG_EXP = new RegExp(
	'(' +
		// Protocol identifier or short syntax "//"
		// a. Full form http://user@foo.bar.baz:8080/foo/bar.html#baz?foo=bar
		'(' +
		'(?:(?:(?:https?|ftp):)?\\/\\/)' +
		// BasicAuth using user:pass (optional)
		'(?:\\S+(?::\\S*)?@)?' +
		'(?:' +
		// IP address dotted notation octets
		// excludes loopback network 0.0.0.0
		// excludes reserved space >= 224.0.0.0
		// excludes network & broadcast addresses
		// (first & last IP address of each class)
		'(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
		'(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
		'(?:\\.(?:25[0-4]|2[0-4]\\d|1\\d\\d|[1-9]\\d?))' +
		'|' +
		'(' +
		// Do not allow `www.foo` - see https://github.com/ckeditor/ckeditor5/issues/8050.
		'((?!www\\.)|(www\\.))' +
		// Host & domain names.
		'(?![-_])(?:[-_a-z0-9\\u00a1-\\uffff]{1,63}\\.)+' +
		// TLD identifier name.
		'(?:[a-z\\u00a1-\\uffff]{2,63})' +
		')' +
		'|' +
		// Allow localhost as a valid hostname
		'localhost' +
		')' +
		// port number (optional)
		'(?::\\d{2,5})?' +
		// resource path (optional)
		'(?:[/?#]\\S*)?' +
		')' +
		'|' +
		// b. Short form (either www.example.com or example@example.com)
		'(' +
		'(www.|(\\S+@))' +
		// Host & domain names.
		'((?![-_])(?:[-_a-z0-9\\u00a1-\\uffff]{1,63}\\.))+' +
		// TLD identifier name.
		'(?:[a-z\\u00a1-\\uffff]{2,63})' +
		')' +
		')',
	'gi'
);


// Function that finds all URLs and their positions
const findURLs = (text: string) => {
	// Initialize
	const results = [];
	let match;
	
	
	// Iterate if a match is found
	while ((match = URL_REG_EXP.exec(text)) !== null) {
		// Initialize
		const url = match[0];
		const startIndex = match.index;
		
		
		// Add to results
		results.push({ url: url, startIndex: startIndex, endIndex: startIndex + url.length });
	}
	
	
	// Reset index
	URL_REG_EXP.lastIndex = 0;
	
	
	// Return
	return results;
};


// Function that normalizes URLs
const normalizeURL = (url: string) => {
	// Handle email addresses
	if (url.includes('@') && !url.startsWith('mailto:')) {
		return `mailto:${url}`;
	}
	
	
	// Add protocol if missing
	const urlWithProtocol = url.startsWith('www.') ? `https://${url}` : url;
	
	
	// Return encoded URL
	try {
		return encodeURI(urlWithProtocol).replace(/%25([0-9A-Fa-f]{2})/g, '%$1');
	} catch {
		return urlWithProtocol;
	}
};


// Function that turns nodes into a link
const linkifyNodes = (nodes: ViewNode[], viewDocument: ViewDocument) => {
	// Initialize
	const results: ViewNode[] = [];
	
	
	// Iterate over nodes
	for (const node of nodes) {
		// Check if this is an element
		if (node.is('element')) {
			// Skip if already a link
			if (node.name === 'a') {
				results.push(node);
				continue;
			}
			
			
			// Initialize
			const children = Array.from(node.getChildren());
			const newChildren = linkifyNodes(children, viewDocument);
			
			
			// Skip rebuilding the element if nothing changed
			const hasChanged = newChildren.some((newChild, i) => newChild !== children[i]);
			
			if (!hasChanged) {
				results.push(node);
				continue;
			}
			
			
			// Initialize
			const newElement = new ViewElement(viewDocument, node.name, node.getAttributes());
			
			
			// Add children to new element
			for (const newChild of newChildren) {
				newElement._appendChild(newChild);
			}
			
			
			// Add new element to results
			results.push(newElement);
			continue;
		}
		
		
		// Skip if not a text node
		if (!node.is('$text')) {
			results.push(node);
			continue;
		}
		
		
		// Initialize
		const text = node.data;
		const matches = findURLs(text);
		
		
		// Skip if no URLs found
		if (!matches.length) {
			results.push(node);
			continue;
		}
		
		
		// Initialize
		let lastIndex = 0;
		
		
		// Iterate over matches
		for (const match of matches) {
			// Add any text before URL
			if (match.startIndex > lastIndex) {
				results.push(new ViewText(viewDocument, text.slice(lastIndex, match.startIndex)));
			}
			
			
			// Initialize
			const normalizedURL = normalizeURL(match.url);
			
			
			// Create link element
			const linkElement = new ViewElement(viewDocument, 'a', new Map([['href', normalizedURL]]));
			linkElement._appendChild(new ViewText(viewDocument, match.url));
			
			
			// Add element and move forward
			results.push(linkElement);
			lastIndex = match.endIndex;
		}
		
		
		// Add any remaining text after last URL
		if (lastIndex < text.length) {
			results.push(new ViewText(viewDocument, text.slice(lastIndex)));
		}
	}
	
	
	// Return results
	return results;
};


// Plugin
export default class AutolinkOnPaste extends Plugin {
	// Handle plugin initialization
	init() {
		// Initialize
		const editor = this.editor;
		const viewDocument = editor.editing.view.document;
		
		
		// Intercept pasted content before it's inserted into the editor
		editor.plugins.get('ClipboardPipeline').on(
			'inputTransformation',
			(
				_event,
				data: {
					method: string;
					content: ViewDocumentFragment | null;
				}
			) => {
				// Return early
				if (data.method !== 'paste' || !data.content) {
					return;
				}
				
				
				// Create a new document fragment to hold the normalized content
				const newFragment = new ViewDocumentFragment(viewDocument);
				
				
				// Get children of pasted content
				const children = Array.from(data.content.getChildren());
				
				
				// Linkify nodes
				const linkifiedNodes = linkifyNodes(children, viewDocument);
				
				
				// Iterate and add nodes
				for (const linkifiedNode of linkifiedNodes) {
					newFragment._appendChild(linkifiedNode);
				}
				
				
				// Set content to be inserted
				data.content = newFragment;
			},
			{ priority: 'normal' }
		);
	}
	
	
	// The name this plugin can be referred to by
	public static get pluginName(): 'AutolinkOnPaste' {
		return 'AutolinkOnPaste';
	}
}
